import { ArrowDownward, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Typography } from "@mui/material";
import { type FC, type ReactNode, useId } from "react";
import { FONT_SIZE, ICON_SIZE, INK, MOTION, NEUTRAL } from "../../../theme/tokens";
import { EyebrowLabel } from "../molecules/EyebrowLabel";
import { CollapsibleCardSection } from "./CollapsibleCardSection";
import { chevronSx } from "./collapsibleSectionSx";

/**
 * `Inline` is a caption-sized disclosure that sits inside content (a chat answer's "what I did" line).
 * `Eyebrow` is a full-width header set in the house uppercase label, for a group inside a settings column
 * whose other groups are headed by that label — a `Section` title there would outrank them.
 * `Card` is a raised card whose header carries a leading round icon and trailing meta — one group in a stack of
 * groups (a send batch and the people in it).
 */
export enum CollapsibleSectionVariant {
	Section = "section",
	Inline = "inline",
	Eyebrow = "eyebrow",
	Card = "card",
}

export type CollapsibleSectionProps = {
	/** Already-translated section title — the disclosure's accessible name. */
	title: string;
	open: boolean;
	onToggle: () => void;
	children: ReactNode;
	// DEFAULT: `Section`, the full-width settings header every existing caller renders.
	variant?: CollapsibleSectionVariant;
	/** Decorative glyph before the title (`Inline` and `Card`; `Card` draws it in a brand disc). */
	// DEFAULT: absent = the title alone.
	icon?: ReactNode;
	/**
	 * Already-translated current value, shown muted after the title (`Eyebrow` only): what the group is set
	 * to, so it can stay collapsed without hiding the answer. Part of the header's accessible name.
	 */
	// DEFAULT: absent = the title alone.
	summary?: string;
	/** Id for the title, so a group inside the region can point its `aria-labelledby` at it. */
	// DEFAULT: absent = the title carries no id; nothing inside the region refers to it.
	titleId?: string;
	/** Trailing text in the header, before the chevron (`Card` only), e.g. "Today · 10:00 AM". */
	// DEFAULT: absent = no meta.
	meta?: string;
	dataTestId?: string;
};

const HEADER_SX = {
	[CollapsibleSectionVariant.Section]: { justifyContent: "space-between", width: "100%", p: 0.5, borderRadius: 1, textAlign: "left" },
	[CollapsibleSectionVariant.Inline]: { gap: 0.5, alignSelf: "flex-start", px: 0.5, py: 0.25, mx: -0.5, borderRadius: 1, textAlign: "start" },
	[CollapsibleSectionVariant.Eyebrow]: { gap: 0.75, width: "100%", px: 0.5, py: 0.25, mx: -0.5, borderRadius: 1, textAlign: "start" },
} as const;

const HeaderContent: FC<Pick<CollapsibleSectionProps, "title" | "open" | "icon" | "summary" | "titleId"> & { variant: CollapsibleSectionVariant }> = ({
	variant,
	title,
	open,
	icon,
	summary,
	titleId,
}) => {
	if (variant === CollapsibleSectionVariant.Eyebrow) {
		return (
			<>
				<EyebrowLabel component="span" id={titleId} sx={{ flexShrink: 0 }}>
					{title}
				</EyebrowLabel>
				{summary !== undefined && (
					<Typography
						component="span"
						sx={{ minWidth: 0, color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
					>
						{summary}
					</Typography>
				)}
				<ExpandMore aria-hidden sx={[chevronSx(open), { fontSize: ICON_SIZE.MICRO, marginInlineStart: "auto" }]} />
			</>
		);
	}
	if (variant === CollapsibleSectionVariant.Inline) {
		return (
			<>
				{icon !== undefined && (
					<Box component="span" aria-hidden sx={{ display: "inline-flex", "& > svg": { fontSize: ICON_SIZE.MICRO } }}>
						{icon}
					</Box>
				)}
				<Typography component="span" id={titleId} sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, fontWeight: 500 }}>
					{title}
				</Typography>
				<ExpandMore aria-hidden sx={[chevronSx(open), { fontSize: ICON_SIZE.MICRO }]} />
			</>
		);
	}
	return (
		<>
			<Typography variant="body2" id={titleId} sx={{ color: "primary.dark", fontWeight: 600 }}>
				{title}
			</Typography>
			<ArrowDownward aria-hidden sx={[chevronSx(open), { fontSize: { xs: 16, xl: 20 } }]} />
		</>
	);
};

/**
 * A titled disclosure: a header row that expands and collapses the block under it.
 *
 * For stacking a long settings surface into named groups where each group is arbitrary content rather than
 * a fixed control type — `FacetFilterSection` is the checkbox-list flavour of the same idea, and this is the
 * content-agnostic one.
 *
 * The header is a native `<button>` so it is reachable and operable from the keyboard, and carries
 * `aria-expanded`/`aria-controls` so assistive technology can report the state and find the region. The
 * chevron is decorative only — it must NOT be a nested `<button>`, which is invalid inside one. The region
 * stays mounted while collapsed, so state inside it (a half-typed field, an open dialog) survives a toggle.
 */
export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
	title,
	open,
	onToggle,
	children,
	variant = CollapsibleSectionVariant.Section,
	icon,
	summary,
	titleId,
	meta,
	dataTestId,
}) => {
	const regionId = useId();

	if (variant === CollapsibleSectionVariant.Card) {
		return (
			<CollapsibleCardSection
				title={title}
				open={open}
				onToggle={onToggle}
				icon={icon}
				titleId={titleId}
				meta={meta}
				regionId={regionId}
				dataTestId={dataTestId}
			>
				{children}
			</CollapsibleCardSection>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			<Box
				component="button"
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={regionId}
				data-testid={dataTestId}
				sx={{
					display: "flex",
					alignItems: "center",
					...HEADER_SX[variant],
					border: "none",
					background: "none",
					font: "inherit",
					cursor: "pointer",
					transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
					"&:hover": { backgroundColor: NEUTRAL[100] },
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}}
			>
				<HeaderContent variant={variant} title={title} open={open} icon={icon} summary={summary} titleId={titleId} />
			</Box>
			<Collapse in={open} id={regionId}>
				{children}
			</Collapse>
		</Box>
	);
};
